import { AlertTriangle, LoaderCircle } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../components/dialog";
import type { OutboxNotificationDetail } from "../types/outboxNotifications.types";

type OutboxNotificationDetailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detail: OutboxNotificationDetail | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
};

const formatTimestamp = (value: string | null): string => {
  if (!value) return "—";

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

export function OutboxNotificationDetailDialog({
  open,
  onOpenChange,
  detail,
  loading,
  error,
  onRetry,
}: OutboxNotificationDetailDialogProps) {
  const { t } = useTranslation();
  const formattedPayload = useMemo(() => {
    if (!detail) return "";
    if (!detail.payloadValid) return detail.payloadJson;

    try {
      return JSON.stringify(JSON.parse(detail.payloadJson), null, 2);
    } catch {
      return detail.payloadJson;
    }
  }, [detail]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("outbox_notifications.detail.title")}</DialogTitle>
          <DialogDescription>{t("outbox_notifications.detail.description")}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
            {t("outbox_notifications.detail.loading")}
          </div>
        ) : error ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
            <p className="text-sm text-destructive">{error}</p>
            <Button type="button" variant="outline" size="sm" className="mt-3" onClick={onRetry}>
              {t("outbox_notifications.detail.retry")}
            </Button>
          </div>
        ) : detail ? (
          <div className="space-y-6">
            <section className="grid gap-3 rounded-lg border p-4 sm:grid-cols-2">
              <DetailItem label={t("outbox_notifications.detail.channel")} value={detail.channel} />
              <DetailItem label={t("outbox_notifications.detail.type")} value={detail.type} />
              <DetailItem label={t("outbox_notifications.detail.attempts")} value={detail.attempts.toString()} />
              <DetailItem label={t("outbox_notifications.detail.created_at")} value={formatTimestamp(detail.createdAt)} />
              <DetailItem label={t("outbox_notifications.detail.updated_at")} value={formatTimestamp(detail.updatedAt)} />
              <DetailItem label={t("outbox_notifications.detail.sent_at")} value={formatTimestamp(detail.sentAt)} />
              <DetailItem label={t("outbox_notifications.detail.next_attempt")} value={formatTimestamp(detail.nextAttemptAt)} />
              <DetailItem label={t("outbox_notifications.detail.last_error")} value={detail.lastError ?? "—"} />
            </section>

            <section>
              <h3 className="text-sm font-semibold">{t("outbox_notifications.detail.user.title")}</h3>
              <div className="mt-2 rounded-lg border p-4 text-sm">
                {detail.user ? (
                  <div className="space-y-1">
                    <p className="font-medium">{detail.user.name}</p>
                    <p className="text-muted-foreground">{detail.user.email}</p>
                    <p className="break-all text-xs text-muted-foreground">{detail.user.id}</p>
                  </div>
                ) : t("outbox_notifications.detail.not_available")}
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold">{t("outbox_notifications.detail.organization.title")}</h3>
              <div className="mt-2 rounded-lg border p-4 text-sm">
                {detail.organization ? (
                  <div className="space-y-1">
                    <p className="font-medium">{detail.organization.name}</p>
                    <p className="break-all text-xs text-muted-foreground">{detail.organization.id}</p>
                  </div>
                ) : t("outbox_notifications.detail.not_available")}
              </div>
            </section>

            <section>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold">{t("outbox_notifications.detail.payload.title")}</h3>
                {!detail.payloadValid && <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />}
              </div>
              {!detail.payloadValid && <p className="mt-1 text-xs text-amber-700">{t("outbox_notifications.detail.payload.invalid")}</p>}
              <pre className="mt-2 max-h-72 overflow-auto rounded-lg bg-slate-950 p-4 text-xs text-slate-100">{formattedPayload}</pre>
            </section>
          </div>
        ) : <p className="py-8 text-center text-sm text-muted-foreground">{t("outbox_notifications.detail.not_available")}</p>}
      </DialogContent>
    </Dialog>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 break-words text-sm">{value}</p>
    </div>
  );
}
